import React, { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, Search, X } from 'lucide-react';
import { BlattFuss, BlattHintergrund, BlattKopf, amRechner } from './FilterBlatt';
import { CATEGORY_LABELS, EVENT_TYPE_LABELS, EVENT_TYPES_BY_CATEGORY, EVENT_TYPES_CROSS_DISCIPLINE, EVENT_CATEGORY_ORDER } from '@radevents/shared';

/**
 * Feld „Eventart“ der Suchzeile am Rechner (27.10.2026), neben Ort und Zeitraum. Vorher stand die
 * Eventart nur hinter „Filter“, obwohl sie auf den Disziplin-Seiten der vierthäufigste Filter ist;
 * 24h-Rennen oder Ultracycling waren über die Startseite praktisch nicht zu finden.
 *
 * Das Menü hat oben eine Suche („24h“), darunter die Arten nach Disziplin. Ohne gewählte Disziplin
 * stehen zuerst die Formate über alle Disziplinen (`EVENT_TYPES_CROSS_DISCIPLINE`), mit Disziplin
 * nur deren Arten. Mehrere Arten sind möglich; das Menü bleibt dafür offen.
 */
interface Props {
  /** Gewählte Disziplin; leer = alle. */
  kategorie: string;
  gewaehlt: string[];
  onWechsel: (arten: string[]) => void;
  /** Als kleiner Knopf in der Zeile unter Ort und Zeitraum statt als großes Feld. */
  pille?: boolean;
  /** Trefferzahl für den Knopf unten im Blatt (Handy). */
  treffer?: number;
}

const label = (t: string) => EVENT_TYPE_LABELS[t] ?? t;
// Ohne Akzente und Leerzeichen: „24h“ findet „24 h Rennen“.
const norm = (s: string) => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, '');

const EventartFeld: React.FC<Props> = ({ kategorie, gewaehlt, onWechsel, pille, treffer }) => {
  const [offen, setOffen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const eingabe = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!offen) { setQ(''); return; }
    if (amRechner()) eingabe.current?.focus();
    const zu = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOffen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOffen(false); };
    document.addEventListener('mousedown', zu);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', zu); document.removeEventListener('keydown', esc); };
  }, [offen]);

  const umschalten = (t: string) => onWechsel(gewaehlt.includes(t) ? gewaehlt.filter(x => x !== t) : [...gewaehlt, t]);
  const passt = (t: string) => !q.trim() || norm(label(t)).includes(norm(q.trim()));

  // Gruppen: ohne Disziplin zuerst die übergreifenden Formate, dann jede Disziplin ohne diese.
  const gruppen: { titel: string; arten: string[] }[] = kategorie
    ? [{ titel: CATEGORY_LABELS[kategorie as keyof typeof CATEGORY_LABELS] ?? kategorie, arten: EVENT_TYPES_BY_CATEGORY[kategorie] || [] }]
    : [
      { titel: 'Über alle Disziplinen', arten: EVENT_TYPES_CROSS_DISCIPLINE },
      ...EVENT_CATEGORY_ORDER.map(k => ({
        titel: CATEGORY_LABELS[k as keyof typeof CATEGORY_LABELS] ?? k,
        arten: (EVENT_TYPES_BY_CATEGORY[k] || []).filter(t => !EVENT_TYPES_CROSS_DISCIPLINE.includes(t)),
      })),
    ];
  const sichtbar = gruppen.map(g => ({ ...g, arten: g.arten.filter(passt) })).filter(g => g.arten.length > 0);

  const wert = gewaehlt.length === 0 ? 'Alle' : gewaehlt.length === 1 ? label(gewaehlt[0]) : `${label(gewaehlt[0])} +${gewaehlt.length - 1}`;

  return (
    <div ref={ref} className={pille ? 'filter-pille-wrap' : 'such-feld-wrap'}>
      {pille ? (
        <button type="button" className={`filter-pille${gewaehlt.length ? ' filter-pille--an' : ''}`} aria-expanded={offen} aria-haspopup="dialog"
          onClick={() => setOffen(o => !o)}>
          {gewaehlt.length ? wert : 'Eventart'}
          {gewaehlt.length === 0 && <ChevronDown size={15} aria-hidden />}
        </button>
      ) : (
        <button type="button" className={`such-feld${gewaehlt.length ? ' such-feld--aktiv' : ''}`} aria-expanded={offen} aria-haspopup="dialog"
          onClick={() => setOffen(o => !o)}>
          <span className="such-feld-text">
            <span className="such-feld-label">Eventart</span>
            <span className="such-feld-wert">{wert}</span>
          </span>
          {gewaehlt.length === 0 && <ChevronDown size={16} aria-hidden className="such-feld-pfeil" />}
        </button>
      )}
      {gewaehlt.length > 0 && (
        <button type="button" className={pille ? 'filter-pille-x' : 'such-feld-x'} onClick={() => { onWechsel([]); setOffen(false); }} aria-label="Eventart zurücksetzen"><X size={pille ? 14 : 16} aria-hidden /></button>
      )}
      {offen && <BlattHintergrund onClose={() => setOffen(false)} />}
      {offen && (
        <div className="ort-menue art-menue" role="dialog" aria-label="Eventart">
          <BlattKopf titel="Eventart" onClose={() => setOffen(false)} />
          <div className="ort-eingabe">
            <Search size={16} aria-hidden />
            <input ref={eingabe} value={q} onChange={e => setQ(e.target.value)} placeholder="Eventart suchen, z. B. 24h"
              aria-label="Eventart suchen"
              onKeyDown={e => { if (e.key === 'Enter' && sichtbar[0]) { umschalten(sichtbar[0].arten[0]); setQ(''); } }} />
            {q && <button type="button" className="ort-leeren" onClick={() => setQ('')} aria-label="Eingabe leeren"><X size={14} aria-hidden /></button>}
          </div>
          <div className="art-liste">
            {sichtbar.length === 0 && <p className="art-leer">Keine Eventart passt zu „{q.trim()}“.</p>}
            {sichtbar.map(g => (
              <div key={g.titel} className="art-gruppe">
                <div className="art-gruppe-titel">{g.titel}</div>
                {g.arten.map(t => {
                  const an = gewaehlt.includes(t);
                  return (
                    <button key={t} type="button" role="menuitemcheckbox" aria-checked={an} className={an ? 'art-zeile art-zeile--an' : 'art-zeile'} onClick={() => umschalten(t)}>
                      <span>{label(t)}</span>
                      {an && <Check size={16} aria-hidden />}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
          <BlattFuss treffer={treffer} onZuruecksetzen={gewaehlt.length ? () => onWechsel([]) : undefined} onFertig={() => setOffen(false)} />
        </div>
      )}
    </div>
  );
};

export default EventartFeld;
